<template>
    <section class="emailDraft" :aria-label="t('chat.emailDraft.label')" data-testid="email-draft">
        <BaseTabs v-model="activeTab" :tab-ids="tabIds" :labels="tabLabels" hide-single-tab-list list-class="emailDraftTabs">
            <template v-for="(variant, index) in variants" :key="`content-${index}`" #[`content-${index}`]>
                <p v-if="variant.subject" class="emailDraftSubject">
                    <span class="scaffold-details-small text-ink-muted">{{ t("chat.emailDraft.subject") }}</span>
                    <span class="emailDraftSubjectValue body-small-semibold text-ink">{{ variant.subject }}</span>
                </p>
                <p class="emailDraftBody body-small text-ink" data-testid="email-draft-body">{{ variant.body }}</p>
                <div class="emailDraftActions">
                    <a
                        v-if="canOpenInMail(variant)"
                        :href="mailtoUrl(variant)"
                        class="base-btn-neutral base-btn-sm emailDraftAction"
                        data-testid="email-draft-open-in-mail"
                        @click="logOpenInMail(index)">
                        {{ t("chat.emailDraft.openInMail") }}
                    </a>
                    <BaseButton theme="primary" size="sm" class="emailDraftAction" data-testid="email-draft-copy" @click="copyEmail(variant, index)">
                        {{ isCopied && copiedIndex === index ? t("chat.emailDraft.copied") : t("chat.emailDraft.copy") }}
                    </BaseButton>
                </div>
            </template>
        </BaseTabs>
        <p class="sr-only" role="status" data-testid="email-draft-copy-status">{{ copyStatus }}</p>
    </section>
</template>

<script setup lang="ts">
import { emailDraftPlainSubject, emailDraftPlainText } from "~vue/ChatDetail/emailDraftPlainText";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import type { EmailDraftVariant, ParseActionsEmailDraftAction } from "~vue/types/chat";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

type ChatEmailDraftProps = {
    data: ParseActionsEmailDraftAction;
    messageId?: number;
    lineIdx: number;
};

const { data, messageId, lineIdx } = defineProps<ChatEmailDraftProps>();

const { t } = useI18n();
const coachingSessionUuid = inject<string>("coachingSessionUuid");
const { show: showCopied, isShowing: isCopied } = useTimedShow({ duration: 2000 });

// The model sometimes writes the draft with inline markdown or HTML despite the prompt, and a mail
// client would show that markup literally, so the card renders, copies and mails one normalised text.
const variants = computed<EmailDraftVariant[]>(() =>
    data.action_params.variants.map((variant) => ({
        ...variant,
        subject: emailDraftPlainSubject(variant.subject),
        body: emailDraftPlainText(variant.body),
    })),
);
const tabIds = computed(() => variants.value.map((_, index) => `${index}`));
const tabLabels = computed(() => Object.fromEntries(variants.value.map((variant, index) => [`${index}`, variant.label || t("chat.emailDraft.draftTab", { index: index + 1 })])));
const activeTab = ref("0");

const emailText = (variant: EmailDraftVariant) => (variant.subject ? t("chat.emailDraft.copyBody", { subject: variant.subject, body: variant.body }) : variant.body);

const mailtoUrl = (variant: EmailDraftVariant) => `mailto:?subject=${encodeURIComponent(variant.subject)}&body=${encodeURIComponent(variant.body)}`;

// Mail clients and browsers cap mailto: around 2000 characters and truncate silently past it, so a
// long draft offers Copy email alone rather than a link that would drop the end of the body.
const canOpenInMail = (variant: EmailDraftVariant) => mailtoUrl(variant).length <= 2000;

const copiedIndex = ref<number | null>(null);
const copyStatus = computed(() => (isCopied.value ? t("chat.emailDraft.copyStatus") : ""));

const copyEmail = async (variant: EmailDraftVariant, index: number) => {
    await navigator.clipboard.writeText(emailText(variant));
    copiedIndex.value = index;
    showCopied();
    logUserInteraction("email_draft_copied", { chat_message_id: messageId, line_idx: lineIdx, variant_index: index, variant_count: variants.value.length }, coachingSessionUuid);
};

const logOpenInMail = (index: number) => {
    logUserInteraction("email_draft_opened_in_mail", { chat_message_id: messageId, line_idx: lineIdx, variant_index: index }, coachingSessionUuid);
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* mx-2 matches the px-2 inset on the tool/message content wrapper so this directive-rendered
       card lines up with the agent-tool cards (role-play, follow-up) in the conversation. */
    .emailDraft {
        @apply mx-2 my-4 w-full max-w-[600px] overflow-hidden rounded-lgr border border-card-cream-border bg-surface-raised;
    }

    .emailDraftSubject {
        @apply flex flex-col gap-1 border-b border-card-cream-border px-4 py-4;
    }

    /* min-w-0 lets the flex item shrink so a long unbroken subject wraps instead of overflowing the card. */
    .emailDraftSubjectValue {
        @apply min-w-0 break-words;
    }

    /* A long draft scrolls inside the card rather than pushing the rest of the conversation off screen. */
    .emailDraftBody {
        @apply max-h-96 overflow-y-auto overscroll-contain px-4 py-4 break-words whitespace-pre-wrap;
    }

    .emailDraftActions {
        @apply flex flex-wrap justify-end gap-2 px-4 pb-4;
    }

    .emailDraftAction {
        @apply inline-flex items-center gap-2;
    }

    /* px-4 lines the first tab label up with the subject and body text below it. */
    :deep(.emailDraftTabs) {
        @apply px-4 pt-2;
    }
}
</style>
